'use client';

import { useEffect, useState, useCallback, Suspense } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { tmdb } from '@/lib/tmdb';
import { rankCandidates } from '@/lib/searchRanking';
import { Header } from '@/components/Header';
import { MediaCard } from '@/components/MediaCard';
import { PersonCard } from '@/components/PersonCard';
import { UserCard } from '@/components/UserCard';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Search,
  ArrowLeft,
  Film,
  Tv,
  AlertCircle
} from 'lucide-react';

interface SearchResult {
  id?: number;
  uid?: string;
  title?: string;
  name?: string;
  username?: string;
  displayName?: string;
  poster_path?: string | null;
  profile_path?: string | null;
  photoURL?: string | null;
  backdrop_path?: string | null;
  vote_average?: number;
  overview?: string;
  release_date?: string;
  first_air_date?: string;
  media_type?: 'tv' | 'movie' | 'person' | 'user';
  known_for_department?: string;
}

function SearchContent() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const query = searchParams.get('q') || '';

  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [totalResults, setTotalResults] = useState(0);

  const searchContent = useCallback(async (searchQuery: string, pageNum: number = 1, reset: boolean = true) => {
    if (!searchQuery.trim()) return;

    if (pageNum === 1) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }

    try {
      // Search TMDB multi + dedicated person search (more accurate than the
      // old 50-page popular-people scan that fired 50 API calls per query
      // and missed anyone outside the top 1000 popular people).
      const promises = [tmdb.searchMulti(searchQuery, pageNum)];

      if (pageNum === 1) {
        promises.push(
          tmdb
            .searchPeople(searchQuery, 1)
            .then((data: any) => ({ results: data.results || [] }))
            .catch(() => ({ results: [] }))
        );
      }

      // Search users on first page with higher limit for search page
      if (pageNum === 1) {
        promises.push(
          fetch(`/api/users/search?q=${encodeURIComponent(searchQuery)}&limit=50`)
            .then(res => res.json())
            .catch(() => ({ users: [] }))
        );
      }

      const results = await Promise.all(promises);
      const data = results[0];
      const peopleData = pageNum === 1 ? results[1] : null;
      const usersData = pageNum === 1 ? results[2] : null;

      // Filter and process TMDB multi results (movies, TV shows, people)
      const tmdbResults = data.results
        .filter((item: any) => {
          // On pagination (pageNum > 1), exclude people entirely - we already have all popular people on page 1
          if (pageNum > 1 && item.media_type === 'person') {
            return false;
          }
          return (item.media_type === 'movie' && item.poster_path && item.poster_path.trim() !== '') ||
            (item.media_type === 'tv' && item.poster_path && item.poster_path.trim() !== '') ||
            (item.media_type === 'person' && item.profile_path && item.profile_path.trim() !== '');
        })
        .map((item: any) => ({
          ...item,
          media_type: item.media_type
        }));

      // Add people from popular people search (only on first page)
      let allPeople = tmdbResults.filter((item: any) => item.media_type === 'person');

      if (peopleData && peopleData.results) {
        // Remove duplicates from popular people API first (using Set to track IDs)
        const seenIds = new Set();
        const uniquePopularPeople = peopleData.results.filter((person: any) => {
          if (seenIds.has(person.id)) {
            return false;
          }
          seenIds.add(person.id);
          return true;
        });

        const additionalPeople = uniquePopularPeople
          .filter((person: any) => person.profile_path && person.profile_path.trim() !== '')
          .map((person: any) => ({
            ...person,
            media_type: 'person' as const
          }))
          .filter((person: any) =>
            // Don't add duplicates from multi-search
            !allPeople.some((p: any) => p.id === person.id)
          );

        // Put popular people FIRST so they appear before less relevant results
        allPeople = [...additionalPeople, ...allPeople];
      }

      // Rank both kinds against the query (handles typos / word order / popularity).
      const moviesAndTv = tmdbResults.filter((item: any) => item.media_type !== 'person');
      const rankedPeople = rankCandidates(allPeople, searchQuery);
      const rankedMedia = rankCandidates(moviesAndTv, searchQuery);
      const combinedTmdb = [...rankedPeople, ...rankedMedia];

      // Process user results (only on first page)
      const userResults = pageNum === 1 && usersData
        ? (usersData.users || []).map((user: any) => ({
            uid: user.uid,
            username: user.username,
            displayName: user.displayName,
            photoURL: user.photoURL,
            media_type: 'user' as const
          }))
        : [];

      const processedResults = [...userResults, ...combinedTmdb];

      if (reset) {
        setResults(processedResults);
        // Calculate total count
        const totalCount = combinedTmdb.length + userResults.length;
        setTotalResults(totalCount);
      } else {
        // When appending (page 2+), avoid duplicates
        setResults(prev => {
          const existingIds = new Set(
            prev.map(item => `${item.id || item.uid}-${item.media_type}`)
          );
          const newResults = processedResults.filter(item => {
            const key = `${item.id || item.uid}-${item.media_type}`;
            return !existingIds.has(key);
          });
          return [...prev, ...newResults];
        });
      }

      setHasMore(pageNum < data.total_pages);
      setPage(pageNum);
    } catch (error) {
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  const loadMore = useCallback(() => {
    if (!loadingMore && hasMore && query) {
      searchContent(query, page + 1, false);
    }
  }, [searchContent, query, page, loadingMore, hasMore]);

  useEffect(() => {
    if (query) {
      searchContent(query, 1, true);
    } else {
      setLoading(false);
    }
  }, [query, searchContent]);


  if (!query) {
    return (
      <div className="min-h-screen bg-black">
        <Header showSearch={true} transparent={true} page="detail" />
        <div className="pt-28 sm:pt-28 md:pt-32 container mx-auto px-4">
          <div className="text-center py-16">
            <AlertCircle className="h-16 w-16 text-gray-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-white mb-4">No Search Query</h1>
            <p className="text-gray-400 mb-6">Please enter a search term to find movies, TV shows, people, and users.</p>
            <Button onClick={() => router.push('/')} variant="outline">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Home
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black">
      <Header showSearch={true} transparent={true} page="detail" />

      <div className="pt-28 sm:pt-28 md:pt-32 container mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
        {/* Header Section */}
        <div className="mb-8 sm:mb-12">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 mb-6">
            <Button
              variant="ghost"
              onClick={() => router.push('/')}
              className="text-gray-400 hover:text-white hover:bg-white/10 p-2 self-start"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-gradient-to-br from-blue-600/20 to-blue-800/20 border border-blue-500/30 flex items-center justify-center backdrop-blur-sm">
                <Search className="h-6 w-6 sm:h-8 sm:w-8 text-blue-500" />
              </div>
              <div>
                <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white">
                  Search Results
                </h1>
                <p className="text-base sm:text-lg text-gray-300 mt-1">
                  Showing results for "{query}"
                </p>
                {!loading && (
                  <Badge variant="outline" className="mt-2 border-blue-500/50 text-blue-400 bg-blue-950/30 w-fit">
                    {totalResults} {totalResults === 1 ? 'result' : 'results'} found
                  </Badge>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Content Grid */}
        {loading ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 2xl:grid-cols-8 gap-3 sm:gap-6">
            {Array.from({ length: 20 }).map((_, i) => (
              <div key={i} className="space-y-3">
                <Skeleton className="aspect-[2/3] w-full rounded-xl bg-gray-800" />
                <Skeleton className="h-4 w-full bg-gray-800" />
                <Skeleton className="h-3 w-3/4 bg-gray-800" />
              </div>
            ))}
          </div>
        ) : results.length > 0 ? (
          <>
            {/* Users Section */}
            {results.filter(item => item.media_type === 'user').length > 0 && (
              <div className="mb-12">
                <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
                  <div className="w-1 h-8 bg-purple-500 rounded-full"></div>
                  Users
                  <Badge variant="outline" className="border-purple-500/50 text-purple-400 bg-purple-950/30">
                    {results.filter(item => item.media_type === 'user').length}
                  </Badge>
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 2xl:grid-cols-7 gap-3 sm:gap-4">
                  {results.filter(item => item.media_type === 'user').map((item) => (
                    <div key={`user-${item.uid}`} className="w-full">
                      <UserCard
                        user={{
                          uid: item.uid!,
                          username: item.username!,
                          displayName: item.displayName,
                          photoURL: item.photoURL
                        }}
                        size="medium"
                        className="!w-full"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* People Section */}
            {results.filter(item => item.media_type === 'person').length > 0 && (
              <div className="mb-12">
                <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
                  <div className="w-1 h-8 bg-orange-500 rounded-full"></div>
                  People
                  <Badge variant="outline" className="border-orange-500/50 text-orange-400 bg-orange-950/30">
                    {results.filter(item => item.media_type === 'person').length}
                  </Badge>
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 2xl:grid-cols-7 gap-3 sm:gap-4">
                  {results.filter(item => item.media_type === 'person').map((item) => (
                    <div key={`${item.id}-person`} className="w-full">
                      <PersonCard
                        person={{
                          id: item.id!,
                          name: item.name!,
                          profile_path: item.profile_path,
                          known_for_department: item.known_for_department
                        }}
                        size="medium"
                        className="!w-full"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Movies Section */}
            {results.filter(item => item.media_type === 'movie').length > 0 && (
              <div className="mb-12">
                <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
                  <div className="w-1 h-8 bg-blue-500 rounded-full"></div>
                  Movies
                  <Badge variant="outline" className="border-blue-500/50 text-blue-400 bg-blue-950/30">
                    {results.filter(item => item.media_type === 'movie').length}
                  </Badge>
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 2xl:grid-cols-7 gap-3 sm:gap-4">
                  {results.filter(item => item.media_type === 'movie').map((item) => (
                    <div key={`${item.id}-movie`} className="w-full">
                      <MediaCard
                        item={item}
                        mediaType="movie"
                        showAddButton={true}
                        size="medium"
                        className="!w-full"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* TV Shows Section */}
            {results.filter(item => item.media_type === 'tv').length > 0 && (
              <div className="mb-12">
                <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
                  <div className="w-1 h-8 bg-green-500 rounded-full"></div>
                  TV Shows
                  <Badge variant="outline" className="border-green-500/50 text-green-400 bg-green-950/30">
                    {results.filter(item => item.media_type === 'tv').length}
                  </Badge>
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 2xl:grid-cols-7 gap-3 sm:gap-4">
                  {results.filter(item => item.media_type === 'tv').map((item) => (
                    <div key={`${item.id}-tv`} className="w-full">
                      <MediaCard
                        item={item}
                        mediaType="tv"
                        showAddButton={true}
                        size="medium"
                        className="!w-full"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Load More Button - for Movies & TV Shows */}
            {hasMore && !loadingMore && (results.filter(item => item.media_type === 'movie' || item.media_type === 'tv').length > 0) && (
              <div className="flex justify-center py-8">
                <Button
                  onClick={loadMore}
                  variant="outline"
                  className="border-blue-500/50 text-blue-400 hover:bg-blue-950/30 hover:text-blue-300"
                >
                  Load More Movies & TV Shows
                </Button>
              </div>
            )}

            {/* Loading More Indicator */}
            {loadingMore && (
              <div className="flex justify-center py-8">
                <div className="flex items-center gap-2 text-gray-400">
                  <div className="animate-spin rounded-full h-6 w-6 border-2 border-blue-500 border-t-transparent"></div>
                  <span>Loading more results...</span>
                </div>
              </div>
            )}

            {/* No more content */}
            {!hasMore && results.length > 0 && (
              <div className="text-center py-8">
                <p className="text-gray-400">You've reached the end of search results! 🔍</p>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-16">
            <Search className="h-16 w-16 text-gray-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-white mb-4">No Results Found</h2>
            <p className="text-gray-400 mb-6">
              We couldn't find any movies, TV shows, people, or users matching "{query}".
              Try searching with different keywords or check your spelling.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button onClick={() => router.push('/')} variant="outline">
                <Film className="h-4 w-4 mr-2" />
                Browse Popular
              </Button>
              <Button onClick={() => router.push('/browse/trending')} variant="outline">
                <Tv className="h-4 w-4 mr-2" />
                View Trending
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function SearchPage() {
  return (
    <Suspense fallback={
      <div className="min-h-screen bg-black">
        <Header showSearch={true} transparent={true} page="detail" />
        <div className="pt-24 sm:pt-20 md:pt-20 container mx-auto px-4">
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 2xl:grid-cols-8 gap-3 sm:gap-6">
            {Array.from({ length: 20 }).map((_, i) => (
              <div key={i} className="space-y-3">
                <Skeleton className="aspect-[2/3] w-full rounded-xl bg-gray-800" />
                <Skeleton className="h-4 w-full bg-gray-800" />
                <Skeleton className="h-3 w-3/4 bg-gray-800" />
              </div>
            ))}
          </div>
        </div>
      </div>
    }>
      <SearchContent />
    </Suspense>
  );
}